import { useContext, useEffect, useMemo, useState } from 'react';
import { NavigateOptions, useLocation, useNavigate } from 'react-router';

import { TicketContext } from '../../contexts/TicketContext';
import { FCWithChildren } from '../../interfaces/Shared';
import { setNextStepGA } from '../../services/Analytics';
import { usePrevious } from '../../services/hooks/usePrevious';
import StepsService from '../../services/StepsService';
import { StepModel } from './interfaces/Step';
import { StepsContext, StepsUpdateContext } from './StepsContext';

const StepsProvider: FCWithChildren = ({ children }) => {
  const { isInErrorMode, ticket } = useContext(TicketContext);
  const defaultSteps = useMemo(() => StepsService.getDefaultSteps(ticket, isInErrorMode), [ticket, isInErrorMode]);
  const [steps, setSteps] = useState<StepModel[]>(defaultSteps);
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const previousInErrorMode = usePrevious<boolean>(isInErrorMode);

  useEffect(() => {
    if (typeof previousInErrorMode !== 'boolean' || previousInErrorMode === isInErrorMode) {
      return;
    }

    setSteps(prevSteps => {
      return isInErrorMode
        ? StepsService.getStepsAs(prevSteps, { isDisabled: true })
        : defaultSteps;
    });
  }, [defaultSteps, isInErrorMode, previousInErrorMode]);

  useEffect(() => {
    setSteps(prevSteps => {
      if (prevSteps.length === 0 && defaultSteps.length > 0) {
        return defaultSteps;
      }

      return prevSteps;
    });
  }, [defaultSteps]);

  const getCurrentStepIndex = (): number => steps.findIndex(item => item.uri === pathname);

  const completeCurrentStep = (): void => {
    const newSteps = [...steps];
    const index = getCurrentStepIndex();
    const nextStepIndex = index + 1;

    if (nextStepIndex < steps.length) {
      newSteps[index] = { ...newSteps[index], isCompleted: true, isDisabled: false };
      newSteps[nextStepIndex] = { ...newSteps[nextStepIndex], isDisabled: false };
      setNextStepGA(newSteps[nextStepIndex].id);
      setSteps(newSteps);
    }
  };

  const navigateToNextStep = (navigationOptions?: NavigateOptions): void => {
    const nextStepIndex = getCurrentStepIndex() + 1;

    if (nextStepIndex < steps.length) {
      navigate(steps[nextStepIndex].uri, navigationOptions);
    }
  };

  const navigateToPrevStep = (): void => {
    const prevStepIndex = getCurrentStepIndex() - 1;

    if (prevStepIndex >= 0) {
      navigate(steps[prevStepIndex].uri);
    }
  };

  const navigateToActiveStep = (): void => {
    const activeStep = StepsService.getActiveStep(steps);

    navigate(activeStep.uri);
  };

  const store = { steps };

  return (
    <StepsContext value={store}>
      <StepsUpdateContext value={{
        completeCurrentStep,
        navigateToActiveStep,
        navigateToNextStep,
        navigateToPrevStep,
      }}>
        {children}
      </StepsUpdateContext>
    </StepsContext>
  );
};

export { StepsProvider };
